import React from "react";
import moment from "moment";
import { round, startCase } from "lodash";
import { withTranslation } from "react-i18next";
import {
  getNutritionPlan,
  getTargetData,
} from "../../../../utils/nutrition-helpers";

class AdherenceStats extends React.Component {
  constructor(props) {
    super(props);

    this.getAdherenceData = this.getAdherenceData.bind(this);
    this.getSpanClass = this.getSpanClass.bind(this);
    this.evaluateValue = this.evaluateValue.bind(this);
    this.evaluatePercent = this.evaluatePercent.bind(this);
    this.smallRowWidth = { width: 85 };
  }

  getAdherenceData() {
    const {
      dateFrom,
      dateTo,
      stats,
      dataView,
      nutritionPlans,
      activeExercises,
    } = this.props;

    const dateFromUtc = moment(dateFrom).format("YYYYMMDD");
    const dateToUtc = moment(dateTo).add(1, "days").format("YYYYMMDD");

    // We pass an empty string to the utcDate as this will always be a period of time.
    const plansInRange = getNutritionPlan(
      nutritionPlans,
      "",
      true,
      dateFromUtc,
      dateToUtc
    );

    const targetData = getTargetData({
      nutritionPlan: plansInRange,
      isPeriod: true,
      startOfWeek: dateFromUtc,
      endOfWeek: dateTo,
      activeExercises,
    });

    const sumArray = stats.reduce(
      (prev, cur) => [
        prev[0] + (cur.nutrients.proteins || 0),
        prev[1] + (cur.nutrients.carbs || 0),
        prev[2] + (cur.nutrients.fats || 0),
        prev[3] + (cur.nutrients.fibers || 0),
      ],
      [0, 0, 0, 0]
    );

    const isDataViewTotal = dataView === "total";

    // if viewing total, use values from sum array, else map to generate an array with the average values
    const [proteins, carbs, fats, fibers] =
      dataView === "total"
        ? sumArray
        : sumArray.map((value) => round(value / (stats.length || 1), 0));

    const { targetProteins, targetFats, targetCarbs, targetFibers } =
      targetData;
    const {
      targetProteins: proteinAvg,
      targetCarbs: carbsAvg,
      targetFats: fatAvg,
      targetFibers: fiberAvg,
    } = getTargetData({
      nutritionPlan: plansInRange,
      isPeriod: true,
      startOfWeek: dateFromUtc,
      endOfWeek: dateToUtc,
      averageValues: true,
      activeExercises,
    });

    const proteinsTarget = isDataViewTotal ? targetProteins : proteinAvg;
    const fatsTarget = isDataViewTotal ? targetFats : fatAvg;
    const carbsTarget = isDataViewTotal ? targetCarbs : carbsAvg;
    const fibersTarget = isDataViewTotal ? targetFibers : fiberAvg;

    const kcal = round(proteins * 4 + carbs * 4 + fats * 9, 0);
    const kcalTarget = round(
      proteinsTarget * 4 + carbsTarget * 4 + fatsTarget * 9,
      0
    );

    return {
      proteins: round(proteins, 0),
      carbs: round(carbs, 0),
      fats: round(fats, 0),
      fibers: round(fibers, 0),
      kcal,
      proteinsTarget: round(proteinsTarget, 0),
      carbsTarget: round(carbsTarget, 0),
      fatsTarget: round(fatsTarget, 0),
      fibersTarget: round(fibersTarget, 0),
      kcalTarget,
      proteinsAdherence: round(proteinsTarget - proteins, 0),
      carbsAdherence: round(carbsTarget - carbs, 0),
      fatsAdherence: round(fatsTarget - fats, 0),
      fibersAdherence: round(fibersTarget - fibers, 0),
      kcalAdherence: round(kcalTarget - kcal, 0),
    };
  }

  getSpanClass(percent) {
    const absoluteValue = Math.abs(percent);
    if (absoluteValue >= 5 && absoluteValue < 10) {
      return "warning";
    } else if (absoluteValue >= 10) {
      return "danger";
    }
    return "regular";
  }

  evaluateValue(value, isKCal) {
    return value ? (
      <>
        {value}
        {isKCal ? "" : "g"}
      </>
    ) : (
      <span>-</span>
    );
  }

  evaluatePercent(percent) {
    if (percent === Infinity || percent === -Infinity) {
      return "-";
    }

    return percent ? `${Math.abs(percent || 0)}%` : "-";
  }

  renderColumn(
    target,
    value,
    adherence,
    AdherencePercent,
    targetPercent,
    consumedPercent,
    title,
    clean = false
  ) {
    const isNotFiberOrKcal = this.isNotFiberOrKcal(title);
    const isKCal = title === "kCal";

    return (
      <section role="table-wrapper">
        <table role="report-table">
          <thead role="table-header">
            <td colSpan={2}>{this.renderRowTitle(title, isKCal)}</td>
          </thead>
          <tbody role="table-body">
            {this.renderRowHeader(clean, title)}
            <tr>
              <td>{this.evaluateValue(target, isKCal)}</td>
              {isNotFiberOrKcal && (
                <td>{this.evaluatePercent(targetPercent)}</td>
              )}
            </tr>
            <tr>
              <td>{this.evaluateValue(value, isKCal)}</td>
              {isNotFiberOrKcal && (
                <td>{this.evaluatePercent(consumedPercent)}</td>
              )}
            </tr>
            {this.renderAdherenceValue(
              adherence,
              AdherencePercent,
              isNotFiberOrKcal,
              !isKCal
            )}
            <tr>
              <td colSpan={2}>{this.renderOverUnder(adherence)}</td>
            </tr>
          </tbody>
        </table>
      </section>
    );
  }

  isNotFiberOrKcal(title) {
    return title !== "fiber" && title !== "kCal";
  }

  renderRowTitle(value, isKCal) {
    const color = `var(--${value})`;

    return <span style={{ color }}>{isKCal ? value : startCase(value)}</span>;
  }

  renderRowHeader(clean = false, title = null) {
    const { t } = this.props;
    const isFiber = title === "fiber";
    const cleanHeader = (
      <tr>
        <th className="border-none!"></th>
      </tr>
    );

    const withData = (
      <tr>
        <th className="border-none!">{t("nutritionPage.adherenceStats.grams")}</th>
        {!isFiber && <th className="border-none!">%</th>}
      </tr>
    );
    return clean ? cleanHeader : withData;
  }

  renderAdherenceValue(value, percent, withPercentage = true, nutrient = true) {
    const renderValue = value ? Math.abs(value) + (nutrient ? "g " : " ") : "-";
    return (
      <tr>
        <td>{renderValue}</td>
        {withPercentage && <td>{this.evaluatePercent(percent)}</td>}
      </tr>
    );
  }

  renderOverUnder(value) {
    return <span>{this.evaluateOverUnder(value)}</span>;
  }

  evaluateOverUnder(value) {
    const { t } = this.props;
    return (
      <span>
        {value > 0
          ? t("nutritionPage.adherenceStats.under")
          : value < 0
            ? t("nutritionPage.adherenceStats.over")
            : "-"}
      </span>
    );
  }

  render() {
    const {
      proteins,
      carbs,
      fats,
      fibers,
      kcal,
      proteinsTarget,
      carbsTarget,
      fatsTarget,
      fibersTarget,
      kcalTarget,
      proteinsAdherence,
      carbsAdherence,
      fatsAdherence,
      fibersAdherence,
      kcalAdherence,
    } = this.getAdherenceData();

    const proteinsAdherencePercent = round(
      (proteinsAdherence / proteinsTarget) * 100,
      0
    );
    const carbsAdherencePercent = round(
      (carbsAdherence / carbsTarget) * 100,
      0
    );
    const fatsAdherencePercent = round((fatsAdherence / fatsTarget) * 100, 0);
    const kcalAdherencePercent = round((kcalAdherence / kcalTarget) * 100, 0);

    const proteinsTargetPercent = round((proteinsTarget * 400) / kcalTarget, 0);
    const carbsTargetPercent = round((carbsTarget * 400) / kcalTarget, 0);
    const fatsTargetPercent = round((fatsTarget * 900) / kcalTarget, 0);

    const proteinsConsumedPercent = round((proteins * 400) / kcal, 0);
    const carbsConsumedPercent = round((carbs * 400) / kcal, 0);
    const fatsConsumedPercent = round((fats * 900) / kcal, 0);

    const { t } = this.props;
    return (
      <div className="adherence-row-stats">
        <div className="adherence-row-titles">
          <label></label>
          <label></label>
          <label>{t("nutritionPage.adherenceStats.target")}</label>
          <label>{t("nutritionPage.adherenceStats.consumed")}</label>
          <label>{t("nutritionPage.adherenceStats.adherence")}</label>
        </div>

        <div className="adherence-clean-body">
          {this.renderColumn(
            proteinsTarget,
            proteins,
            proteinsAdherence,
            proteinsAdherencePercent,
            proteinsTargetPercent,
            proteinsConsumedPercent,
            "protein"
          )}
          {this.renderColumn(
            carbsTarget,
            carbs,
            carbsAdherence,
            carbsAdherencePercent,
            carbsTargetPercent,
            carbsConsumedPercent,
            "carbs"
          )}
          {this.renderColumn(
            fatsTarget,
            fats,
            fatsAdherence,
            fatsAdherencePercent,
            fatsTargetPercent,
            fatsConsumedPercent,
            "fat"
          )}
          {/* Fiber and kCal don't show percentages */}
          {this.renderColumn(
            fibersTarget,
            fibers,
            fibersAdherence,
            0,
            0,
            0,
            "fiber"
          )}
          {this.renderColumn(
            kcalTarget,
            kcal,
            kcalAdherence,
            kcalAdherencePercent,
            0,
            0,
            "kCal",
            true
          )}
        </div>
      </div>
    );
  }
}

export default withTranslation("athlete-portal")(AdherenceStats);
